import { alpha } from "@mui/material";

const theme = {
  // Text Colors
  textColors: {
    light: {
      primary: "#222", // Dark text for light mode
      secondary: "#555",
      disabled: "rgba(34, 34, 34, 0.5)", // Reduced opacity for disabled text
    },
    dark: {
      primary: "#D1D4DC", // Light text for dark mode
      secondary: "#aaa",
      disabled: "#7582a3",
    },
  },

  //Main colors
  main: {
    applicationBackground: "#1e2732",
    primaryBlue: "#443dff",
    secondaryBlue: "#355271",
    tertiaryBlue: "#706bff",
    extremeWhite: "#E5EFFF",
    backgroundBlue: "#253040",
    chatwindow: "#1A1A24",
    extremeBlack: "#111111",
    panel: "#212b38",
  },

  // Background Colors
  backgroundColors: {
    light: {
      primary: "#f8f9fa", // Light theme background
      secondary: "#e0e0e0",
    },
    dark: {
      primary: "#1e2732", // Dark theme background
      secondary: "#25394f", // Secondary background
      tertiary: "#1C1F2A",
      tableButton: {
        primary: "#25394f",
        disabled: "#2d3c4c",
        hover: "#253040",
      },
      blackVariants: {
        primary: "#131417", // Dark theme background
        secondary: "#1B1D22", // Secondary background
        tertiary: "#2a2a2a",
      },
    },
  },

  // Table Styles
  table: {
    background: {
      primary: "#1E2538", // Table container background
      secondary: "#171E30", // Table background
      header: "#1B1D22", // Table header background
      rowHover: "#2A2C33", // Hover effect for table rows - temporary
    },
    text: {
      primary: "#D1D4DC", // Primary text in table
      secondary: "#aaa",
    },
    border: "#2A2D35", // Border color for table elements
    headerText: {
      primary: "#D1D4DC", // Table header text color
      fontWeight: "bold",
    },
    progress: {
      high: "#22c55e", // Green for 80-100
      mediumHigh: "#d97706", // Brownish orange for 70-80
      medium: "#eab308", // Yellow for 50-70
      low: "#ef4444", // Red for 0-50
    },
    statusButton: {
      background: "#202732",
      color: "#63b3ed",
      border: "#2a2d35",
    },
  },

  // Form Styles
  // inputs: {
  //   background: "#131417", // Form container background
  //   inputBackground: "#1B1D22", // Input field background
  //   selectBackground: "#1B1D22", // Select dropdown background
  //   labelColor: "#D1D4DC", // Label text color
  //   borderColor: "#2A2D35", // Default border color
  //   hoverBorderColor: "#3A3D45", // Border color on hover
  //   focusBorderColor: "#5A5D65", // Border color on focus
  //   iconColor: "#D1D4DC", // Icon color for dropdowns
  // },

  // Input Styles
  inputs: {
    container: "#131417",
    background: "#1B1D22", // Background for input fields
    textColor: "#D1D4DC", // Input text color
    borderColor: "#2A2D35", // Default input border color
    hoverBorderColor: "#3A3D45", // Border color when hovering
    focusBorderColor: "#5A5D65", // Border color when focused
    placeholderColor: "#aaa", // Placeholder text color
    iconColor: "#D1D4DC", // Icon color for dropdowns
  },

  // Border Colors
  border: {
    primary: "#27272a", // General primary border color
    secondary: "#1A1A1B", // Secondary border color
    divider: "#2A2D35", // Divider line color
    light: "#3b4654",
  },

  // Font Styles
  typography: {
    fontFamily: "Inter, Arial, sans-serif", // Default font family
    fontSize: {
      xxs: "9px", // Extra extra small text for sub-texts
      xs: "12px", // Extra small text
      sm: "14px", // Small text
      md: "16px", // Medium text
      lg: "18px", // Large text
      xl: "20px", // Extra large text
    },
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      bold: 700,
    },
  },

  // screen breakpoints
  breakpoints: {
    values: {
      xs: 0, // Extra small screens, typically for mobile
      sm: 600, // Small screens, tablets
      md: 960, // Medium screens, small laptops
      lg: 1280, // Large screens, laptops and desktops
      xl: 1920, // Extra large screens, large desktops
    },

    // Breakpoint functions: up() and down() logic
    up: (key) => `@media (min-width:${theme.breakpoints.values[key]}px)`,
    down: (key) => `@media (max-width:${theme.breakpoints.values[key]}px)`,
  },

  // State Colors (for status indicators)
  state: {
    error: { primary: "#d32f2f", secondary: "#942121", disabled: "#ff9c9c" },
    warning: { primary: "#ed6c02", secondary: "#ff9800", disabled: "#e65100" },
    info: { primary: "#0288d1", secondary: "#03a9f4", disabled: "#01579b" },
    success: { primary: "#2e7d32", secondary: "#4caf50", disabled: "#1b5e20" },
  },

  // Border Radius Sizes
  borderRadius: {
    lg: "12px", // Large rounded corners
    md: "8px", // Medium rounded corners
    sm: "4px", // Small rounded corners
  },

  divider: {
    horizontal: {
      backgroundColor: "#2A2D35",
      margin: "12px 0",
    },
    veritcal: {
      backgroundColor: "#2A2D35",
      margin: "0 12px",
    },
  },

  // Utility Functions
  utils: {
    getDisabledColor: (color) => `${color}cc`, // Adds 80% opacity to any color

    getHoverColor: (color, mode = "dark") => {
      // Simple hover color adjustment
      const adjust = (hex, amount) => {
        let usePound = false;
        if (hex[0] === "#") {
          hex = hex.slice(1);
          usePound = true;
        }

        let num = parseInt(hex, 16);
        let r = (num >> 16) + amount;
        let g = ((num >> 8) & 0x00ff) + amount;
        let b = (num & 0x0000ff) + amount;

        r = Math.max(Math.min(255, r), 0);
        g = Math.max(Math.min(255, g), 0);
        b = Math.max(Math.min(255, b), 0);

        return (
          (usePound ? "#" : "") +
          ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
        );
      };

      // Darken in light mode, lighten in dark mode
      const amount = mode === "light" ? -15 : 15;
      return adjust(color, amount);
    },
    getAccuracyColor: (accuracy) => {
      if (accuracy >= 80) return theme.table.progress.high;
      if (accuracy >= 70) return theme.table.progress.mediumHigh;
      if (accuracy >= 50) return theme.table.progress.medium;
      return theme.table.progress.low;
    },
  },

  // Chat Messages colors
  chatMessagesColors: {
    primary: "#3C3F51",
    secondary: "#27293D",
  },
};

export const scrollbarStyles = {
  scrollbarWidth: "thin",
  // Uses primaryBlue for the thumb and dark secondary for the track
  scrollbarColor: `${theme.backgroundColors.dark.tableButton.primary} ${theme.table.background.secondary}`,
  "-webkit-overflow-scrolling": "touch",

  // WebKit browsers (Chrome, Safari, Edge)
  "&::-webkit-scrollbar": {
    width: "8px",
    height: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.main.secondaryBlue, // Primary thumb color
    borderRadius: theme.borderRadius.sm, // Consistent rounded corners
    transition: "background-color 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: theme.main.tertiaryBlue, // Lighter blue on hover
    },
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.backgroundColors.dark.blackVariants.secondary, // Track color
    borderRadius: theme.borderRadius.sm,
  },

  "@media screen and (hover: none) and (pointer: coarse)": {
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
};

export const tabsStyle = {
  // Container adjustments
  px: 1,
  bgcolor: "transparent",
  // Override the default indicator (we’ll use background instead)
  "& .MuiTabs-indicator": {
    display: "none",
  },
  // Add margin-right on every tab except the last one
  "& .MuiTab-root:not(:last-of-type)": {
    mr: 2, // MUI spacing unit → 2 * 8px = 16px
  },

  // Each tab
  "& .MuiTab-root": {
    position: "relative",
    color: theme.textColors.dark.primary,
    textTransform: "none",
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    minWidth: 100,
    px: 2,
    py: 1,
    borderRadius: 2,
    transition: "background-color 0.2s",
    // Hover state
    "&:hover": {
      backgroundColor: alpha(theme.main.primaryBlue, 0.15),
    },
    // Selected state
    "&.Mui-selected": {
      backgroundColor: alpha(theme.main.primaryBlue, 0.3),
      fontWeight: 500,
      zIndex: 1,
      color: theme.textColors.dark.primary,
    },
  },
};

export const createLLMTypography = {
  fontSizes: {
    title: theme.typography.fontSize.lg,
    sectionTitle: theme.typography.fontSize.md,
    sectionText1: theme.typography.fontSize.sm,
    sectionText2: theme.typography.fontSize.xs,
    infoText: theme.typography.fontSize.xxs,
  },
  colors: {
    inputsBackground: "#DCDCDC",
    inputsTextColor: theme.main.extremeBlack,
  },
};

export default theme;
